import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

interface ExportOptions {
  fileName: string;
  title: string;
  subtitle?: string;
}

export function useExportMetrics() {
  const exportToExcel = (
    data: Record<string, any>[],
    columns: { key: string; header: string }[],
    options: ExportOptions
  ) => {
    const worksheetData = data.map(row => {
      const newRow: Record<string, any> = {};
      columns.forEach(col => {
        newRow[col.header] = row[col.key];
      });
      return newRow;
    });

    const worksheet = XLSX.utils.json_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Datos');

    // Auto-size columns
    const colWidths = columns.map(col => ({
      wch: Math.max(col.header.length, 15)
    }));
    worksheet['!cols'] = colWidths;

    XLSX.writeFile(workbook, `${options.fileName}.xlsx`);
  };

  const exportToPDF = (
    data: Record<string, any>[],
    columns: { key: string; header: string }[],
    options: ExportOptions,
    summaryStats?: { label: string; value: string | number }[]
  ) => {
    const doc = new jsPDF();
    
    // Title
    doc.setFontSize(18);
    doc.setTextColor(40, 40, 40);
    doc.text(options.title, 14, 22);
    
    // Subtitle
    if (options.subtitle) {
      doc.setFontSize(11);
      doc.setTextColor(100, 100, 100);
      doc.text(options.subtitle, 14, 30);
    }

    // Date
    doc.setFontSize(10);
    doc.setTextColor(150, 150, 150);
    doc.text(`Generado: ${new Date().toLocaleDateString('es-ES', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })}`, 14, options.subtitle ? 38 : 30);

    let startY = options.subtitle ? 45 : 38;

    // Summary stats
    if (summaryStats && summaryStats.length > 0) {
      doc.setFontSize(12);
      doc.setTextColor(40, 40, 40);
      doc.text('Resumen', 14, startY);
      startY += 8;

      summaryStats.forEach((stat, index) => {
        doc.setFontSize(10);
        doc.setTextColor(80, 80, 80);
        doc.text(`${stat.label}: ${stat.value}`, 14, startY + (index * 6));
      });

      startY += summaryStats.length * 6 + 10;
    }

    // Table
    const tableHeaders = columns.map(col => col.header);
    const tableData = data.map(row => columns.map(col => String(row[col.key] ?? '')));

    autoTable(doc, {
      head: [tableHeaders],
      body: tableData,
      startY,
      styles: {
        fontSize: 9,
        cellPadding: 3,
      },
      headStyles: {
        fillColor: [79, 70, 229],
        textColor: 255,
        fontStyle: 'bold',
      },
      alternateRowStyles: {
        fillColor: [245, 245, 250],
      },
      margin: { left: 14, right: 14 },
    });

    doc.save(`${options.fileName}.pdf`);
  };

  return { exportToExcel, exportToPDF };
}
